import { Bell, Mic, Volume2 } from 'lucide-react';
import { DevicesPermissionPanel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DevicesTab/DevicesPermissionPanel';
import { DevicesSection } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DevicesTab/DevicesSection';
import { DevicesSoundTest } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DevicesTab/DevicesSoundTest';
import { useSoftphoneDevices } from 'Modules/softphone/hooks/useSoftphoneDevices';
import { useSoftphoneStore } from 'Modules/softphone/store';
import React, { useCallback, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

/**
 * DevicesTab - What audio hardware is in play
 */
export const DevicesTab: React.FC = () => {
  const {
    microphones,
    speakers,
    ringers,
    selectedMicrophone,
    selectedSpeaker,
    selectedRinger,
    permissionState,
    audioPlaying,
  } = useSoftphoneStore(
    useShallow((state) => ({
      microphones: state.microphones,
      speakers: state.speakers,
      ringers: state.ringers,
      selectedMicrophone: state.selectedMicrophone,
      selectedSpeaker: state.selectedSpeaker,
      selectedRinger: state.selectedRinger,
      permissionState: state.permissionState,
      audioPlaying: state.audioPlaying,
    }))
  );

  const { refreshDevices } = useSoftphoneDevices();
  const [isRequesting, setIsRequesting] = useState(false);

  // `refreshDevices` prompts when the permission is undecided, so this is the
  // one button that can move the tab out of its "labels are empty" state.
  const handleRequest = useCallback(() => {
    setIsRequesting(true);

    void refreshDevices().finally(() => setIsRequesting(false));
  }, [refreshDevices]);

  return (
    <div className="flex flex-col gap-3">
      <DevicesPermissionPanel
        state={permissionState}
        microphoneCount={microphones.length}
        speakerCount={speakers.length}
        ringerCount={ringers.length}
        onRequest={handleRequest}
        isRequesting={isRequesting}
      />

      <DevicesSoundTest audioPlaying={audioPlaying} />

      <DevicesSection
        title="Microphones"
        icon={<Mic className="h-[13px] w-[13px]" strokeWidth={1.9} />}
        devices={microphones}
        selectedId={selectedMicrophone}
      />

      <DevicesSection
        title="Speakers"
        icon={<Volume2 className="h-[13px] w-[13px]" strokeWidth={1.9} />}
        devices={speakers}
        selectedId={selectedSpeaker}
        defaultExpanded={false}
      />

      <DevicesSection
        title="Ringers"
        icon={<Bell className="h-[13px] w-[13px]" strokeWidth={1.9} />}
        devices={ringers}
        selectedId={selectedRinger}
        defaultExpanded={false}
      />
    </div>
  );
};
